import { styled } from '@mui/material';

import { AIResponse, SimpleStatus } from '@app-velocity/common';

import { InfoOutlineIcon } from '@app-velocity/icons';
import { useI18n } from '@app-velocity/i18n';

import { StatusBackground } from '../StatusBackground';
import { Tooltip } from '../Tooltip';
import { Typography } from '../Typography';

export type AiResponseConfidenceProps = {
  response: AIResponse;
};

const typeToStatus: Record<string, SimpleStatus> = {
  high: 'success',
  medium: 'warning',
  low: 'error',
};

const StyledConfidence = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.spacing(2),
  overflow: 'hidden',
  padding: theme.spacing(4),
  color: 'var(--text-text-alt-color)',
  zIndex: 0,
}));

const ConfidenceHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
}));

const ConfidenceTitle = styled(Typography)({
  fontSize: 14,
  fontWeight: 500,
});

const ConfidenceDescription = styled(Typography)({
  fontSize: 14,
  lineHeight: 1.2,
  fontWeight: 300,
});

export const AiResponseConfidence = ({
  response,
}: AiResponseConfidenceProps) => {
  const { t } = useI18n();
  const { confidence, confidenceExplanation } = response;

  if (!confidence) return null;

  return (
    <StyledConfidence>
      <StatusBackground status={typeToStatus[confidence]} noPattern />
      <ConfidenceHeader>
        <ConfidenceTitle>
          {t(`prototypes:confidence_score.${confidence}`)}
        </ConfidenceTitle>
        <Tooltip
          title={`${t('prototypes:confidence_score.tooltip')} ${t(
            `prototypes:confidence_score.tooltip_${confidence}`
          )}`}
        >
          <InfoOutlineIcon />
        </Tooltip>
      </ConfidenceHeader>
      {confidenceExplanation ? (
        <ConfidenceDescription>{confidenceExplanation}</ConfidenceDescription>
      ) : null}
    </StyledConfidence>
  );
};
